import { Button } from '@/components/common/Button';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import InputField from '@/components/common/InputField';
import Modal from '@/components/common/modals/Modal';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { assert } from '@common/errors';
import { OnboardingInfoBeneficiary } from '@common/types';
import { getUuid } from '@common/util/uuid';
import { useState } from 'react';
import { labelByRelationship } from './helpers';

interface BeneficiaryModalProps {
    title: string;
    save: (beneficiary: OnboardingInfoBeneficiary) => void;
    beneficiary?: OnboardingInfoBeneficiary;
}

export const showBeneficiaryModal = (props: BeneficiaryModalProps) =>
    ModalManager.show(BeneficiaryModal, props);

const BeneficiaryModal = ModalManager.create<BeneficiaryModalProps>(
    ({ save, beneficiary, title }) => {
        useTrackModalShown('Beneficiary');

        const modal = useModal();
        const id = beneficiary?.id ?? getUuid();
        const [firstName, setFirstName] = useState(beneficiary?.firstName);
        const [lastName, setLastName] = useState(beneficiary?.lastName);
        const [relationship, setRelationship] = useState(
            beneficiary?.relationship
        );
        const [dob, setDob] = useState(beneficiary?.dob);

        const isComplete = id && firstName && lastName && relationship && dob;

        return (
            <Modal
                size="md"
                isOpen={modal.visible}
                onClose={modal.remove}
                title={title}
                renderFooter={() => (
                    <FlexRow
                        justifyContent="flex-end"
                        alignItems="center"
                        p={5}
                    >
                        <Button
                            variant="link"
                            colorScheme="gray"
                            onClick={modal.remove}
                            onClickEvent="Cancel Beneficiary Button Clicked"
                            mr={16}
                        >
                            Cancel
                        </Button>
                        <Button
                            isDisabled={!isComplete}
                            onClick={() => {
                                assert(isComplete);
                                save({
                                    id,
                                    firstName,
                                    lastName,
                                    relationship,
                                    dob,
                                    fraction:
                                        beneficiary?.fraction ??
                                        // N.B. If `beneficiary` is undefined, we're
                                        // creating a new beneficiary, and rebalance
                                        // logic will replace this arbitrary value.
                                        1,
                                });
                                modal.remove();
                            }}
                            onClickEvent="Save Beneficiary Button Clicked"
                        >
                            Save beneficiary
                        </Button>
                    </FlexRow>
                )}
            >
                <FlexCol gap={8}>
                    <FlexRow gap={4}>
                        <InputField
                            type="text"
                            name="firstName"
                            label="First name"
                            value={firstName}
                            onChange={setFirstName}
                            inputOptions={{ toUpperFirst: true }}
                        />
                        <InputField
                            type="text"
                            name="lastName"
                            label="Last name"
                            value={lastName}
                            onChange={setLastName}
                            inputOptions={{ toUpperFirst: true }}
                        />
                    </FlexRow>
                    <FlexRow gap={4}>
                        <InputField
                            type="select"
                            name="relationship"
                            label="Relationship"
                            options={Object.entries(labelByRelationship).map(
                                ([value, label]) => ({ value, label })
                            )}
                            value={relationship}
                            onChange={setRelationship}
                        />
                        <InputField
                            type="date"
                            name="dob"
                            label="Date of birth"
                            value={dob}
                            onChange={setDob}
                        />
                    </FlexRow>
                </FlexCol>
            </Modal>
        );
    }
);

export default BeneficiaryModal;
